import { keepPreviousData, QueryClient, QueryKey } from '@tanstack/query-core'
import {
  InfiniteData,
  infiniteQueryOptions,
  mutationOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import axios, { AxiosRequestConfig } from 'axios'
import { produce } from 'immer'
import queryString from 'query-string'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BulkTransactionUpdateSuccessToastMessage } from 'src/app/Toast/Toast'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { PeopleContactsResponse } from 'src/models/people.models'
import {
  ReceiptVerificationStatus,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'
import {
  TransactionAiInsightFeedbackType,
  TransactionAiInsights,
  TransactionDetailModel,
  TransactionDetailResponse,
  TransactionListModel,
  TransactionListResponse,
} from 'src/models/transaction.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { closeModal } from 'src/navigation/Lnk'
import { startAsyncJobPoll } from 'src/queries/asyncJobs/asyncJobPoll.store'
import { QueryError } from 'src/queries/network.utils'
import { ExpenseCategoryLabelFiltersBody } from 'src/queries/organizations/expenseCategories.queries'
import {
  ORG_EXPENSE_REPORTS_QK,
  transactionsQueryKeys,
  USERS_QK,
} from 'src/queries/queries.constants'
import {
  CustomMutationOptions,
  ExtendBaseInfiniteQueryOptions,
  ExtendBaseQueryOptions,
  getListQueryKeys,
  getNextExtendPageParam,
  getNextTransactionPageParam,
  MutationMethod,
  useExtendDelete,
  useExtendMutation,
  useRequestConfig,
} from 'src/queries/queries.utils'
import {
  getTransactionDetail,
  getTransactionExpenseCategoryLabels,
  getTransactions,
  getTransactionsCount,
  putTransactionAiInsightFeedback,
  PutTransactionAiInsightFeedbackRequest,
  TransactionExpenseCategoryLabelResponse,
  TransactionExpenseCategoryLabelsResponse,
  TransactionsResponse,
} from 'src/queries/transactions.network'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import {
  PatchBulkTransactionExpenseDataRequest,
  PatchTransactionExpenseDataRequest,
} from 'src/requests/patchTransactionExpenseData.request'
import { PostSingleTransactionExpenseCategoryRequest } from 'src/requests/postSingleTransactionExpenseCategory.request'
import { UpdateTransactionExpenseDetailsRequest } from 'src/requests/updateTransactionExpenseDetails.request'

// Response types now live in transactions.network.ts (single owner for the
// network boundary); re-exported here for existing cross-family consumers.
export type { TransactionsResponse }

const selectTransactionDetailModel = (
  data: TransactionDetailResponse,
): TransactionDetailModel => new TransactionDetailModel(data)

// Note that these models are built uniquely using the more general PaginationModel, but don't extend it
// because we currently have tech debt where certain responses don't return fully accurate pagination data
// i.e. we can't get totalItems or numberOfPages for Transactions Reports
export interface TransactionsDataModel {
  transactions: TransactionListModel[] | undefined
  page: number
}

const transactionsListModelSelectCache = new WeakMap<
  InfiniteData<TransactionsResponse, number>,
  TransactionsDataModel
>()

export const selectTransactionListModel = (
  response: InfiniteData<TransactionsResponse, number>,
): TransactionsDataModel => {
  const cached = transactionsListModelSelectCache.get(response)
  if (cached) {
    return cached
  }

  const selected: TransactionsDataModel = {
    transactions: response?.pages
      .flatMap((page) =>
        page.report.transactions.map((txn) => new TransactionListModel(txn)),
      )
      // only take first instance of each transaction
      // deal with edge cases that arise from pusher events loading in data realtime
      // TODO: React Query this approach will not handle cases when transactions are deleted locally from the list
      .filter(
        (txn1, i, txns) => txns.findIndex((txn2) => txn2.id === txn1.id) === i,
      ),
    page: response.pageParams.length - 1,
  }

  transactionsListModelSelectCache.set(response, selected)
  return selected
}

/**
 * React Query option factories for transactions reads (migrated off the legacy
 * useExtendQuery family). Each factory reuses the EXACT existing query key from
 * transactionsQueryKeys so the cache is shared with the mutation cache-updaters
 * below and with any remaining legacy readers.
 *
 * Network access lives in transactions.network.ts (getRequestConfig + getResponseData);
 * errors thrown by those functions are normalized (normalizeQueryError) — the same
 * backend payload the legacy wrappers surfaced, plus `status`/`originalError`.
 */
export const transactionQueries = {
  // The queryKey encodes a boolean-driven URL split (your vs. all transactions).
  list: ({
    filters,
    myTransactions = false,
    currentUserId,
    perPage = 50,
    locationKey,
  }: {
    filters: TransactionsFiltersBody | undefined
    myTransactions?: boolean
    currentUserId: string | undefined
    perPage?: number
    locationKey?: string
  }) =>
    infiniteQueryOptions({
      // transactionsQueryKeys.list is annotated as a narrow tuple; widen to the
      // shared QueryKey type so the factory composes with consumer option
      // overrides (parity with virtualCardsQueryKeys, which returns QueryKey).
      queryKey: transactionsQueryKeys.list(filters, locationKey) as QueryKey,
      queryFn: ({ pageParam }) =>
        getTransactions({
          filters,
          page: pageParam,
          perPage,
          myTransactions,
          currentUserId,
        }),
      enabled:
        filters !== undefined &&
        (myTransactions ? currentUserId !== undefined : true),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        getNextTransactionPageParam(
          lastPage.report.page,
          lastPage.report.perPage,
          lastPage.report.count,
        ),
      select: selectTransactionListModel,
      // re-fetches happen in place without resetting back to an empty state, less flashing of content
      placeholderData: keepPreviousData,
      // Preserve useExtendInfiniteQuery's default of not replaying all prior
      // pages on remount (gcTime ?? 0).
      gcTime: 0,
    }),
  detail: (queryClient: QueryClient, transactionId: string | undefined) =>
    queryOptions({
      queryKey: transactionsQueryKeys.detail(transactionId),
      queryFn: async () => {
        if (!transactionId) {
          throw new Error(
            'transactionQueries.detail: transactionId is required',
          )
        }
        const data = await getTransactionDetail(transactionId)
        // Keep list rows in sync with freshly-fetched detail data. The legacy
        // useExtendQuery ran this inside its queryFn (onSuccess); doing it here
        // preserves the same per-fetch cache-sync behaviour.
        updateTransactionListResponses(queryClient, {
          transactionIds: [data.id],
          updater: (existing) => ({
            ...existing,
            ...data,
            receiptMatchingStatus:
              data.receiptMatchingStatus ?? existing.receiptMatchingStatus,
            attachmentsCount:
              data.attachmentsCount ?? existing.attachmentsCount,
            attachments:
              data.attachments ??
              data.receiptAttachments ??
              existing.attachments,
          }),
        })
        return data
      },
      enabled: transactionId !== undefined,
      select: selectTransactionDetailModel,
    }),
  count: (filters: TransactionsFiltersBody | undefined, locationKey?: string) =>
    queryOptions({
      queryKey: transactionsQueryKeys.count(filters, locationKey),
      queryFn: () => getTransactionsCount(filters),
      select: (data) => data.count,
    }),
  expenseCategoryLabels: ({
    transactionId,
    expenseCategoryId,
    filters,
  }: {
    transactionId: string | undefined
    expenseCategoryId: string | undefined
    filters: ExpenseCategoryLabelFiltersBody
  }) =>
    infiniteQueryOptions({
      queryKey: transactionsQueryKeys.expenseCategoryLabels(
        transactionId,
        expenseCategoryId,
        filters,
      ),
      queryFn: ({ pageParam }) => {
        if (!transactionId || !expenseCategoryId) {
          throw new Error(
            'transactionQueries.expenseCategoryLabels: transactionId and expenseCategoryId are required',
          )
        }
        return getTransactionExpenseCategoryLabels({
          transactionId,
          expenseCategoryId,
          filters,
          page: pageParam,
        })
      },
      enabled: expenseCategoryId !== undefined && transactionId !== undefined,
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        getNextExtendPageParam(lastPage.pagination),
      select: (data) => data.pages.flatMap((page) => page.expenseLabels),
      gcTime: 30000,
      staleTime: 30000,
    }),
}

interface UseQueryTransactionsOptions
  extends ExtendBaseInfiniteQueryOptions<
    TransactionsResponse,
    number,
    TransactionsDataModel
  > {
  filters: TransactionsFiltersBody | undefined
  myTransactions?: boolean | undefined
  locationKey?: string
}

export const useQueryTransactions = ({
  filters,
  myTransactions = false,
  enabled = true,
  perPage = 50,
  locationKey,
  ...rest
}: UseQueryTransactionsOptions) => {
  const currentUserId = useSelector(selectUserIdFromState)
  const { queryCacheOptions: _queryCacheOptions, ...queryOptionsRest } = rest
  const query = useInfiniteQuery({
    ...transactionQueries.list({
      filters,
      myTransactions,
      currentUserId,
      perPage,
      locationKey,
    }),
    enabled:
      enabled &&
      filters !== undefined &&
      (myTransactions ? currentUserId !== undefined : true),
    ...queryOptionsRest,
  })

  return {
    ...query,
    // Preserve the synthetic `complete` field the legacy useExtendInfiniteQuery
    // wrapper exposed (consumers gate infinite-scroll / select-all on it).
    complete: !query.hasNextPage && !query.isFetching,
  }
}

// TODO: as part of the filters refactor, we should be able to have a FiltersContext that can be used to
// call useQueryTransactions directly without needing this function
export const useQueryFetchedTransactions = () => {
  const queryClient = useQueryClient()
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    transactionsQueryKeys.lists,
  )

  if (currentQueryKeys.length > 1) {
    // TODO: React Query will we need to support multiple transaction list queries with different filters at once?
    /* eslint-disable-next-line no-console */
    console.warn(
      'useQueryFetchedTransactions: multiple transactions queries detected, using the first one',
    )
  }
  const queryKey = currentQueryKeys[0] ?? []

  // TODO: this is a great candidate for implementing a runtime validation library like zod
  // casting because if the order of the query keys changes, this will throw a type error
  const filters = queryKey.at(-1) as TransactionsFiltersBody | undefined
  const locationKey = typeof queryKey[1] === 'string' ? queryKey[1] : undefined

  return useQueryTransactions({
    filters,
    locationKey,
    refetchOnMount: false,
  })
}

export const updateTransactionListResponses = (
  queryClient: QueryClient,
  options: {
    transactionIds: string[]
    updater?: (transaction: TransactionListResponse) => TransactionListResponse
    newTransactions?: TransactionListResponse[]
  },
  updateMetrics: boolean = true, // only set to false where multiple txns are being updating individually to avoid exessive refetching (currently only usePatchBulkTransactionExpenseData)
) => {
  const { transactionIds, updater, newTransactions } = options
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    transactionsQueryKeys.lists,
  )

  currentQueryKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (
        oldTransactions: InfiniteData<TransactionsResponse, number> | undefined,
      ) => {
        // runtime validation of type for any potential edge cases
        if (!oldTransactions?.pages?.[0]?.report?.transactions)
          return oldTransactions

        const updatedPages: TransactionsResponse[] = oldTransactions.pages.map(
          (page) => {
            return produce(page, (draft) => {
              draft.report.transactions = page.report.transactions.map(
                (txn) => {
                  if (transactionIds?.includes(txn.id)) {
                    // if there's a new transaction, use that
                    return (
                      newTransactions?.find((newTxn) => newTxn.id === txn.id) ??
                      updater?.(txn) ??
                      txn
                    )
                  } else {
                    return txn
                  }
                },
              )
            })
          },
        )

        // If new transactions are provided, we may need to add them to the first page
        if (newTransactions !== undefined) {
          const oldTransactionIds = new Set(
            oldTransactions?.pages.flatMap((page) =>
              page.report.transactions.map((txn) => txn.id),
            ),
          )

          // Filter new transactions that are not already in the old transactions list
          const newTransactionsToAdd = newTransactions.filter(
            (txn) => !oldTransactionIds.has(txn.id),
          )

          if (newTransactionsToAdd.length > 0) {
            // Add the new transactions to the top of the first page
            updatedPages[0] = produce(updatedPages[0], (draft) => {
              draft.report.transactions = [
                ...newTransactionsToAdd,
                ...updatedPages[0].report.transactions,
              ]
            })
          }
        }

        return produce(oldTransactions, (draft) => {
          draft.pages = updatedPages
        })
      },
    )
  })
  if (updateMetrics) {
    // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
    queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
  }
}

const isPendingReceiptVerificationStatus = (
  status: ReceiptVerificationStatus,
): boolean =>
  status === ReceiptVerificationStatus.Verifying ||
  status === ReceiptVerificationStatus.Missing

const preserveReceiptVerification = (
  old: TransactionDetailResponse,
  next: TransactionDetailResponse,
): TransactionDetailResponse => {
  const previousAttachmentsById = new Map(
    old.receiptAttachments.map((attachment) => [attachment.id, attachment]),
  )
  const preserveReceiptMatchingStatus =
    old.receiptMatchingStatus !== undefined &&
    !isPendingReceiptVerificationStatus(old.receiptMatchingStatus) &&
    (next.receiptMatchingStatus === undefined ||
      isPendingReceiptVerificationStatus(next.receiptMatchingStatus))

  return {
    ...next,
    receiptMatchingStatus: preserveReceiptMatchingStatus
      ? old.receiptMatchingStatus
      : next.receiptMatchingStatus,
    receiptAttachments: next.receiptAttachments.map((attachment) => {
      const previousAttachment = previousAttachmentsById.get(attachment.id)
      if (!previousAttachment) return attachment

      const previousVerification =
        resolveAttachmentVerification(previousAttachment)
      const incomingVerification = resolveAttachmentVerification(attachment)
      const shouldPreservePrevious =
        previousVerification !== undefined &&
        (incomingVerification === undefined ||
          (!isPendingReceiptVerificationStatus(previousVerification.status) &&
            isPendingReceiptVerificationStatus(incomingVerification.status)))

      if (!shouldPreservePrevious) return attachment

      return {
        ...attachment,
        verification: previousAttachment.verification,
        matchingStatuses: previousAttachment.matchingStatuses,
      }
    }),
  }
}

export const updateTransactionDetailResponse = (
  queryClient: QueryClient,
  options: {
    transactionId: string
    updater?: (
      transaction: TransactionDetailResponse,
    ) => TransactionDetailResponse
    newTransaction?: TransactionDetailResponse
  },
  updateMetrics?: boolean,
) => {
  const { transactionId, updater, newTransaction } = options

  const updaterSafe = (old: TransactionDetailResponse | undefined) => {
    if (newTransaction) {
      return old
        ? preserveReceiptVerification(old, newTransaction)
        : newTransaction
    }
    if (!old) return undefined
    const updated = updater?.(old)
    return updated ? preserveReceiptVerification(old, updated) : undefined
  }
  // update detail response in cache
  queryClient.setQueryData(
    transactionsQueryKeys.detail(transactionId),
    updaterSafe,
  )
  // update list response in cache

  const listUpdater = updater
    ? (txn: TransactionListResponse) =>
        // pass in stub data to the updater since it's going to throw it out anyway
        updater({ ...txn, receiptAttachments: [], virtualCardId: undefined })
    : undefined

  const listNewTransactions = newTransaction
    ? [
        {
          ...newTransaction,
          attachments:
            newTransaction.attachments ??
            newTransaction.receiptAttachments ??
            undefined,
        },
      ]
    : undefined

  updateTransactionListResponses(
    queryClient,
    {
      transactionIds: [transactionId],
      updater: listUpdater,
      newTransactions: listNewTransactions,
    },
    updateMetrics,
  )
}

// detail screen queries and mutations
interface UseQueryTransactionDetailOptions
  extends ExtendBaseQueryOptions<
    TransactionDetailResponse,
    TransactionDetailModel
  > {
  transactionId: string | undefined
}
export const useQueryTransactionDetail = ({
  transactionId,
  enabled = true,
  ...rest
}: UseQueryTransactionDetailOptions) => {
  const queryClient = useQueryClient()

  return useQuery({
    ...transactionQueries.detail(queryClient, transactionId),
    enabled: enabled && !!transactionId,
    select: selectTransactionDetailModel,
    structuralSharing: (oldData, newData) =>
      oldData
        ? preserveReceiptVerification(
            oldData as TransactionDetailResponse,
            newData as TransactionDetailResponse,
          )
        : newData,
    ...rest,
  })
}

export const queryTransactionDetail = async (
  queryClient: QueryClient,
  config: AxiosRequestConfig,
  transactionId: string,
  updateMetrics?: boolean,
): Promise<TransactionDetailResponse | undefined> => {
  try {
    const transactionResponse = (
      await axios.get<TransactionDetailResponse>(
        `/transactions/${transactionId}`,
        config,
      )
    ).data

    updateTransactionDetailResponse(
      queryClient,
      {
        transactionId: transactionResponse.id,
        newTransaction: transactionResponse,
      },
      updateMetrics,
    )
    return transactionResponse
  } catch (e) {
    /* eslint-disable-next-line no-console */
    console.warn(`Could not get ${transactionId} detail`, e)
    return undefined
  }
}

export interface PutTransactionAiInsightFeedbackVariables {
  transactionId: string
  insightId: string
  body: PutTransactionAiInsightFeedbackRequest
}

export const transactionAiInsightFeedbackMutations = {
  putFeedback: (requestConfig: AxiosRequestConfig) =>
    mutationOptions<
      TransactionAiInsights,
      QueryError,
      PutTransactionAiInsightFeedbackVariables
    >({
      mutationFn: ({ transactionId, insightId, body }) =>
        putTransactionAiInsightFeedback(
          transactionId,
          insightId,
          body,
          requestConfig,
        ),
    }),
}

export function usePutTransactionAiInsightFeedbackMutation(
  options?: CustomMutationOptions<
    TransactionAiInsights,
    QueryError,
    PutTransactionAiInsightFeedbackVariables
  >,
) {
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig() as AxiosRequestConfig
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...transactionAiInsightFeedbackMutations.putFeedback(requestConfig),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.setQueryData<TransactionDetailResponse>(
        transactionsQueryKeys.detail(variables.transactionId),
        (old) => {
          if (!old) return old

          return produce(old, (draft) => {
            const insight = draft.transactionAiInsights?.find(
              (candidate) => candidate.id === data.id,
            )
            if (insight) {
              if (
                data.feedbackType == null ||
                data.feedbackType === TransactionAiInsightFeedbackType.None
              ) {
                delete insight.feedbackType
              } else {
                insight.feedbackType = data.feedbackType
              }
            }
          })
        },
      )
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export const usePatchTransaction = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    { transactionId: string; body: Partial<PatchTransactionRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) => `/transactions/${transactionId}`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: () => data,
      })
    },
  })
}

export const usePatchTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    { transactionId: string; body: Partial<PatchTransactionExpenseDataRequest> }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensecategories`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          expenseDetails: data.expenseDetails,
        }),
      })
    },
  })
}

export const useUpdateTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const useSplitTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PUT, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata/split`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const useUnsplitTransactionExpenseData = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    {
      transactionId: string
      body: UpdateTransactionExpenseDetailsRequest
    }
  >(MutationMethod.PATCH, {
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/expensedata/unsplit`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        newTransaction: data,
      })
    },
  })
}

export const usePatchBulkTransactionExpenseData = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const requestConfig = useRequestConfig()

  return useExtendMutation<
    { jobId?: string },
    {
      filters?: TransactionsFiltersBody
      body: Partial<PatchBulkTransactionExpenseDataRequest>
    }
  >(MutationMethod.PATCH, {
    mutationUrl: (variables) =>
      `/transactions/expensedata/bulkupdate?${queryString.stringify({ ...variables?.filters })}`,
    onSuccess: (data, variables) => {
      dispatch(closeModal())
      // TODO - can remove sync flow once bulk_select_fast_follows flag is removed
      if (data.jobId !== undefined && data.jobId !== null) {
        // async update — start the React Query status poll (was the
        // getAsyncTransactionUpdateJobStatus Redux thunk)
        startAsyncJobPoll(data.jobId)
      } else {
        // sync update
        variables.body.transactionIds?.forEach((transactionId) => {
          // TODO: React Query move to updateTransactionDetailResponse when backend returns the correct data
          queryTransactionDetail(
            queryClient,
            requestConfig,
            transactionId,
            false,
          )
        })
        // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
        queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] }) // TODO: remove once above TODO is resolved
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <BulkTransactionUpdateSuccessToastMessage
                count={variables.body.transactionIds?.length || 0}
              />
            ),
          }),
        )
      }
    },
  })
}

export const useQueryTransactionsPerson = (personId: string | undefined) => {
  const requestConfig = useRequestConfig()

  // leaving this as an example of how we can implement custom queryFn logic if the useExtendQuery doesn't fit the use case
  return useQuery({
    queryKey: [USERS_QK, personId],
    queryFn: async () => {
      return await axios
        .get<PeopleContactsResponse>(
          `/connections?userToId=${personId}`,
          requestConfig,
        )
        .then((response) => {
          if (response.data.userConnections.length > 0) {
            return new UserListModel(response.data.userConnections[0].userTo)
          } else {
            return axios
              .get<UserListResponse>(`/users/${personId}`, requestConfig)
              .then((response) => new UserListModel(response.data))
          }
        })
    },
    enabled: personId !== undefined,
  })
}

// Delete transactions from the list responses for ALL list views
export const deleteTransactionListResponses = (
  queryClient: QueryClient,
  newTransactionIds: string[],
) => {
  const currentQueryKeys = getListQueryKeys(
    queryClient,
    transactionsQueryKeys.lists,
  )
  const lookup = new Set(newTransactionIds)
  currentQueryKeys.forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (
        oldTransactions: InfiniteData<TransactionsResponse, number> | undefined,
      ) => {
        // runtime validation of type for any potential edge cases
        if (!oldTransactions?.pages?.[0]?.report) return oldTransactions
        return {
          ...oldTransactions,
          pages: oldTransactions.pages.map((page) => ({
            ...page,
            report: {
              ...page.report,
              transactions: page.report.transactions.filter(
                (txn) => !lookup.has(txn.id),
              ),
            },
          })),
        }
      },
    )
  })
  // invalidate metrics - we dont know what report the transaction was in, so invalidate all report metrics
  queryClient.invalidateQueries({ queryKey: [ORG_EXPENSE_REPORTS_QK] })
}

export const deleteTransactionDetailResponse = (
  queryClient: QueryClient,
  transactionId: string,
) => {
  // delete detail response in cache
  queryClient.setQueryData(
    transactionsQueryKeys.detail(transactionId),
    undefined,
  )
  // delete list response in cache
  deleteTransactionListResponses(queryClient, [transactionId])
}

export const useDeletePhysicalTransaction = () => {
  const queryClient = useQueryClient()
  return useExtendDelete<TransactionDetailResponse, { transactionId: string }>({
    mutationUrl: ({ transactionId }) => `/transactions/${transactionId}`,
    onSuccess: (_, { transactionId }) => {
      deleteTransactionDetailResponse(queryClient, transactionId)
    },
  })
}

interface UseQueryTransactionsCountOptions
  extends ExtendBaseQueryOptions<{ count: number }, number> {
  filters: TransactionsFiltersBody | undefined
  locationKey?: string
}

export const useSuspenseQueryTransactionsCount = ({
  filters,
  ...rest
}: UseQueryTransactionsCountOptions) => {
  return useSuspenseQuery({
    ...transactionQueries.count(filters),
    ...rest,
  })
}

export const useQueryTransactionsCount = ({
  filters,
  enabled,
  locationKey,
  ...rest
}: UseQueryTransactionsCountOptions) => {
  return useQuery({
    ...transactionQueries.count(filters, locationKey),
    enabled: enabled && filters !== undefined,
    ...rest,
  })
}

export interface PostSingleTransactionExpenseCategoryVariables {
  transactionId: string
  categoryId: string
  body: PostSingleTransactionExpenseCategoryRequest
}

export const usePostSingleTransactionExpenseCategory = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    PostSingleTransactionExpenseCategoryVariables
  >(MutationMethod.POST, {
    mutationKey: transactionsQueryKeys.postSingleExpenseCategory(),
    mutationUrl: ({ transactionId, categoryId }) =>
      `/transactions/${transactionId}/expensedata/categories/${categoryId}`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          expenseDetails: data.expenseDetails,
          managerReviewStatus: data.managerReviewStatus,
        }),
      })
    },
  })
}

interface QueryTransactionExpenseCategoryLabelsOptions
  extends ExtendBaseInfiniteQueryOptions<
    TransactionExpenseCategoryLabelsResponse,
    number,
    TransactionExpenseCategoryLabelResponse[]
  > {
  expenseCategoryId: string | undefined
  transactionId: string | undefined
  filters: ExpenseCategoryLabelFiltersBody
}

export const useQueryTransactionExpenseCategoryLabels = ({
  expenseCategoryId,
  transactionId,
  filters,
  enabled = true,
  ...rest
}: QueryTransactionExpenseCategoryLabelsOptions) => {
  const { queryCacheOptions: _queryCacheOptions, ...queryOptionsRest } = rest
  const query = useInfiniteQuery({
    ...transactionQueries.expenseCategoryLabels({
      transactionId,
      expenseCategoryId,
      filters,
    }),
    enabled:
      enabled && expenseCategoryId !== undefined && transactionId !== undefined,
    ...queryOptionsRest,
  })

  return {
    ...query,
    // Preserve the synthetic `complete` field the legacy wrapper exposed.
    complete: !query.hasNextPage && !query.isFetching,
  }
}
